import React, { useState, useEffect } from "react";
import { IoSearch } from "react-icons/io5";
import { LuDownload } from "react-icons/lu";
import {
  MdOutlineDeleteForever,
  MdOutlineKeyboardArrowRight,
} from "react-icons/md";
import { PiUpload } from "react-icons/pi";
import UploadSignedLetterModal from "../components/UploadSignedLetterModal";
import NotificationModal from "../components/NotificationModal";
import ConfirmationModal from "../components/ConfirmationModal"; // Import the ConfirmationModal

const SignedLetters = () => {
  const [letters, setLetters] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmOpen, setConfirmOpen] = useState(false); // State for confirmation modal
  const [letterToDelete, setLetterToDelete] = useState(null); // ID of the letter to delete
  const [formData, setFormData] = useState({
    applicantName: "",
    department: "",
    letterType: "",
    file: null,
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  //TODO:Comment during production build
  // const apiUrl = "http://localhost:5000/";

  // State for notification modal
  const [notification, setNotification] = useState({
    isOpen: false,
    message: "",
    type: "success", // Default type
  });

  const handleCloseNotification = () => {
    setNotification({ ...notification, isOpen: false });
  };

  const fetchLetters = async () => {
    try {
      const response = await fetch(`${apiUrl}api/signed-letters`);
      const data = await response.json();

      if (Array.isArray(data)) {
        setLetters(data);
      } else {
        console.error("API did not return an array:", data);
        setLetters([]);
      }
    } catch (error) {
      console.error("Failed to fetch letters:", error);
      setLetters([]);
    }
  };

  useEffect(() => {
    fetchLetters();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Check file type and size
    if (file) {
      const isPDF = file.type === "application/pdf";
      const isUnder3MB = file.size <= 3 * 1024 * 1024; // 3MB

      if (!isPDF) {
        setNotification({
          isOpen: true,
          message: "Only PDF files are allowed.",
          type: "error",
        });
        return;
      }

      if (!isUnder3MB) {
        setNotification({
          isOpen: true,
          message: "File size must be less than 3MB.",
          type: "error",
        });
        return;
      }
    }

    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleUpload = async () => {
    // Validating files before uploading
    if (
      !formData.applicantName ||
      !formData.department ||
      !formData.letterType ||
      !formData.file
    ) {
      setNotification({
        isOpen: true,
        message: "Please fill in all fields and upload a file.",
        type: "error",
      });
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("applicantName", formData.applicantName);
    formDataToSend.append("department", formData.department);
    formDataToSend.append("letterType", formData.letterType);
    formDataToSend.append("file", formData.file);

    try {
      const response = await fetch(`${apiUrl}api/upload-signed-letters`, {
        method: "POST",
        body: formDataToSend,
      });

      if (response.ok) {
        setNotification({
          isOpen: true,
          message: "File uploaded successfully!",
          type: "success",
        });
        setModalOpen(false);
        fetchLetters();

        // Clear the form data
        setFormData({
          applicantName: "",
          department: "",
          letterType: "",
          file: null,
        });
      } else {
        const errorData = await response.json(); // Get error message from response
        setNotification({
          isOpen: true,
          message: errorData.error || "Failed to upload file.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      setNotification({
        isOpen: true,
        message: "Failed to upload file. Try again later",
        type: "error",
      });
    }
  };

  const handleDelete = (id) => {
    setLetterToDelete(id); // Set the letter ID to delete
    setConfirmOpen(true); // Open the confirmation modal
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${apiUrl}api/signed-letters/${letterToDelete}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        // Remove the deleted letter from the state
        setLetters((prevLetters) =>
          prevLetters.filter((letter) => letter.id !== letterToDelete)
        );
        setNotification({
          isOpen: true,
          message: "Record deleted successfully!",
          type: "success",
        });
      } else {
        const errorData = await response.json(); // Get error message from response
        setNotification({
          isOpen: true,
          message: errorData.error || "Failed to delete record.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      setNotification({
        isOpen: true,
        message: "Failed to delete record.",
        type: "error",
      });
    } finally {
      setConfirmOpen(false); // Close the confirmation modal
    }
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row justify-between items-center mb-6 lg:pr-14 flex-shrink-0">
        <div className="flex items-center w-full lg:w-2/5 lg:mt-0">
          <MdOutlineKeyboardArrowRight size={34} className="mt-1 ml-4" />
          <h2 className="text-xl ml-4 font-bold text-green-700">
            Signed Internship Letters (Copy)
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row w-full lg:w-4/5 mt-3 lg:mt-0 lg:justify-end space-x-6">
          <div className="flex flex-col lg:flex-row lg:space-x-4 lg:ml-4 mt-2 space-y-2 lg:mt-0 items-center">
            <button
              className="bg-green-700 w-full lg:w-auto text-white max-h-11 px-6 rounded-[10px] py-[10px] font-semibold hover:bg-opacity-75 flex items-center gap-3"
              onClick={() => setModalOpen(true)}
            >
              <PiUpload size={24} />
              Upload New Signed Document
            </button>
          </div>
          <div className="relative w-full lg:w-3/5">
            <input
              type="text"
              className="border border-green-700 max-h-11 rounded-xl px-2 py-[10px] pl-14 w-full"
              placeholder="Search Documents, files..."
            />
            <IoSearch className="absolute left-5 top-3 text-customMaroon text-xl" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto px-10 pb-10">
        <table className="min-w-full table-auto bg-gray-100 shadow-md overflow-hidden rounded-t-2xl">
          <thead>
            <tr className="bg-green-700 bg-opacity-80 text-white ">
              <th className="p-4 text-base font-semibold w-10 rounded-tl-lg">
                #
              </th>
              <th className="py-3 px-2 pl-6 text-left">Applicant Name</th>
              <th className="py-3 px-2 pl-6 text-left">Department</th>
              <th className="py-3 px-2 text-left">Letter Type</th>
              <th className="py-3 px-2 text-left">Date Uploaded</th>
              <th className="py-3 px-6 text-left rounded-tr-lg">Actions</th>
            </tr>
          </thead>

          <tbody>
            {letters.map((letter, index) => (
              <tr
                key={index}
                className="border-b border-b-gray-300 hover:bg-customGold hover:bg-opacity-85 transition duration-200"
              >
                <td className="px-4 py-4 border-b border-gray-300 font-medium">
                  {index + 1}
                </td>
                <td className="px-6 py-4 border-b border-gray-300 font-medium">
                  {letter.applicantName}
                </td>
                <td className="px-6 py-4 border-b border-gray-300 ">
                  {letter.department}
                </td>
                <td className="px-6 py-4 border-b border-gray-300 font-medium">
                  {letter.letterType}
                </td>
                <td className="px-6 py-4 border-b border-gray-300 ">
                  {new Date(letter.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 border-b border-gray-300">
                  <div className="flex items-center  space-x-10">
                    <a
                      href={`${apiUrl}uploads/signed_letters/${letter.fileName}`}
                      download
                      className="text-green-700 hover:text-green-600"
                      title="Download Letter"
                    >
                      <LuDownload size={24} />
                    </a>
                    <button
                      onClick={() => handleDelete(letter.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete Letter"
                    >
                      <MdOutlineDeleteForever size={26} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Upload Modal */}
      <UploadSignedLetterModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onUpload={handleUpload}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        formData={formData}
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={handleCloseNotification}
        message={notification.message}
        type={notification.type}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setConfirmOpen(false)} // Close the modal
        onConfirm={confirmDelete} // Confirm delete action
        title="Confirm Deletion"
        message="Are you sure you want to delete this letter?"
      />
    </div>
  );
};

export default SignedLetters;
